import React, { useState, useEffect } from 'react'
import axios from 'axios'
import { toast } from 'react-hot-toast'
import { FileText, Plus, RefreshCw, Send, Trash2, X, CheckCircle2, Clock, XCircle, FileEdit } from 'lucide-react'
import { getSession } from '../lib/session'

// WhatsApp message templates. They live locally until submitted to Meta; only
// APPROVED templates can be broadcast. Status flows LOCAL → PENDING → APPROVED/REJECTED.
const STATUS = {
  LOCAL:    { label: 'Draft',    cls: 'bg-gray-100 text-gray-500 border-gray-200',   icon: FileEdit },
  PENDING:  { label: 'Pending',  cls: 'bg-amber-500/10 text-amber-600 border-amber-500/20', icon: Clock },
  APPROVED: { label: 'Approved', cls: 'bg-green-500/10 text-green-600 border-green-500/20', icon: CheckCircle2 },
  REJECTED: { label: 'Rejected', cls: 'bg-red-500/10 text-red-600 border-red-500/20',  icon: XCircle },
}

export default function Templates() {
  const session = getSession()
  const [templates, setTemplates] = useState([])
  const [loading, setLoading] = useState(false)
  const [syncing, setSyncing] = useState(false)
  const [busyId, setBusyId] = useState('')
  const [showCreate, setShowCreate] = useState(false)

  const load = () => {
    if (!session?.namespaceId) return
    setLoading(true)
    axios.get('/api/v1/campaigns/templates')
      .then(r => setTemplates(r.data.templates || []))
      .catch(err => toast.error(err.response?.data?.error || 'Failed to load templates'))
      .finally(() => setLoading(false))
  }
  useEffect(load, [session?.namespaceId])

  const sync = async () => {
    setSyncing(true)
    try {
      const r = await axios.post('/api/v1/campaigns/templates/sync')
      toast.success(r.data.note || `Synced ${r.data.synced} template${r.data.synced === 1 ? '' : 's'} from Meta`)
      load()
    } catch (err) { toast.error(err.response?.data?.error || 'Sync failed') }
    finally { setSyncing(false) }
  }

  const submit = async (t) => {
    setBusyId(t.id)
    try {
      const r = await axios.post(`/api/v1/campaigns/templates/${t.id}/submit`)
      setTemplates(list => list.map(x => x.id === t.id ? r.data : x))
      toast.success('Submitted to Meta for approval')
    } catch (err) { toast.error(err.response?.data?.error || 'Submit failed') }
    finally { setBusyId('') }
  }

  const remove = async (t) => {
    if (!window.confirm(`Delete template "${t.name}"?`)) return
    setBusyId(t.id)
    try {
      await axios.delete(`/api/v1/campaigns/templates/${t.id}`)
      setTemplates(list => list.filter(x => x.id !== t.id))
    } catch (err) { toast.error(err.response?.data?.error || 'Delete failed') }
    finally { setBusyId('') }
  }

  return (
    <div className="space-y-8">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2"><FileText size={24} className="text-primary-600" /> Templates</h2>
          <p className="text-gray-500 mt-1">Reusable WhatsApp messages. Meta must approve one before you can broadcast it.</p>
        </div>
        <div className="flex gap-2">
          <button onClick={sync} disabled={syncing} className="btn-secondary text-sm flex items-center gap-2 disabled:opacity-60">
            <RefreshCw size={16} className={syncing ? 'animate-spin' : ''} /> Sync from Meta
          </button>
          <button onClick={() => setShowCreate(true)} className="btn-primary text-sm flex items-center gap-2"><Plus size={16} /> New template</button>
        </div>
      </div>

      {loading && <div className="card text-center text-gray-400 py-10">Loading…</div>}
      {!loading && templates.length === 0 && (
        <div className="card text-center py-12">
          <FileText size={32} className="mx-auto text-gray-300 mb-3" />
          <p className="text-gray-500">No templates yet. Create one, or sync the ones you already have in Meta.</p>
        </div>
      )}

      <div className="grid gap-4 md:grid-cols-2">
        {templates.map(t => {
          const st = STATUS[t.status] || STATUS.LOCAL
          const StIcon = st.icon
          return (
            <div key={t.id} className="card">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="font-semibold text-gray-900 truncate">{t.name}</p>
                  <p className="text-xs text-gray-400 mt-0.5">{t.category} · {t.language}</p>
                </div>
                <span className={`inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full border shrink-0 ${st.cls}`}>
                  <StIcon size={12} /> {st.label}
                </span>
              </div>

              {/* Message preview */}
              <div className="mt-3 rounded-lg bg-gray-50 border border-gray-200 p-3 text-sm space-y-1">
                {t.header_text && <p className="font-semibold text-gray-800">{t.header_text}</p>}
                <p className="text-gray-700 whitespace-pre-wrap">{t.body_text}</p>
                {t.footer_text && <p className="text-xs text-gray-400">{t.footer_text}</p>}
                {Array.isArray(t.buttons) && t.buttons.length > 0 && (
                  <div className="flex flex-wrap gap-1.5 pt-1">
                    {t.buttons.map((b, i) => <span key={i} className="text-xs text-primary-600 border border-primary-600/30 rounded px-2 py-0.5">{b.text}</span>)}
                  </div>
                )}
              </div>

              {t.status === 'REJECTED' && t.rejected_reason && (
                <p className="mt-2 text-xs text-red-600">Rejected: {t.rejected_reason}</p>
              )}

              <div className="mt-3 flex items-center gap-2">
                {t.status !== 'APPROVED' && (
                  <button onClick={() => submit(t)} disabled={busyId === t.id}
                    className="text-sm text-primary-600 hover:text-primary-700 font-medium flex items-center gap-1 disabled:opacity-60">
                    <Send size={14} /> Submit for approval
                  </button>
                )}
                <button onClick={() => remove(t)} disabled={busyId === t.id}
                  className="text-sm text-gray-400 hover:text-red-600 flex items-center gap-1 ml-auto disabled:opacity-60">
                  <Trash2 size={14} /> Delete
                </button>
              </div>
            </div>
          )
        })}
      </div>

      {showCreate && <CreateTemplateModal onClose={() => setShowCreate(false)} onSaved={() => { setShowCreate(false); load() }} />}
    </div>
  )
}

function CreateTemplateModal({ onClose, onSaved }) {
  const [form, setForm] = useState({
    name: '', category: 'MARKETING', language: 'en_US',
    header_text: '', body_text: '', footer_text: '', buttons: '', example_params: '',
  })
  const [busy, setBusy] = useState(false)
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }))

  const submit = async () => {
    if (!form.name.trim()) { toast.error('Template name is required'); return }
    if (!form.body_text.trim()) { toast.error('Template body text is required'); return }
    setBusy(true)
    try {
      // Buttons: one per line as "Label" or "Label | https://url" (URL button).
      const buttons = form.buttons.split(/\r?\n/).map(l => l.trim()).filter(Boolean).map(l => {
        const [text, url] = l.split('|').map(p => p.trim())
        return url ? { text, url } : { text }
      })
      const example_params = form.example_params.split(',').map(p => p.trim()).filter(Boolean)
      await axios.post('/api/v1/campaigns/templates', {
        name: form.name.trim(), category: form.category, language: form.language,
        header_text: form.header_text.trim() || undefined,
        body_text: form.body_text.trim(),
        footer_text: form.footer_text.trim() || undefined,
        buttons, example_params,
      }, { validateStatus: s => s < 500 }).then(r => { if (r.status >= 400) throw new Error(r.data.error) })
      toast.success('Template saved')
      onSaved()
    } catch (err) { toast.error(err.response?.data?.error || err.message || 'Could not save template') }
    finally { setBusy(false) }
  }

  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center p-4 bg-black/40" onClick={onClose}>
      <div className="bg-gray-100 border border-gray-200 rounded-xl w-full max-w-lg p-6 max-h-[90vh] overflow-auto" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900">New template</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X size={20} /></button>
        </div>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <label className="block"><span className="block text-xs font-medium text-gray-500 mb-1">Name *</span>
              <input className="input" value={form.name} onChange={e => set('name', e.target.value)} placeholder="order_update" /></label>
            <label className="block"><span className="block text-xs font-medium text-gray-500 mb-1">Category</span>
              <select className="input" value={form.category} onChange={e => set('category', e.target.value)}>
                <option value="MARKETING">Marketing</option>
                <option value="UTILITY">Utility</option>
                <option value="AUTHENTICATION">Authentication</option>
              </select></label>
          </div>
          <label className="block"><span className="block text-xs font-medium text-gray-500 mb-1">Language</span>
            <input className="input" value={form.language} onChange={e => set('language', e.target.value)} placeholder="en_US" /></label>
          <label className="block"><span className="block text-xs font-medium text-gray-500 mb-1">Header (optional)</span>
            <input className="input" value={form.header_text} onChange={e => set('header_text', e.target.value)} placeholder="A short title" /></label>
          <label className="block"><span className="block text-xs font-medium text-gray-500 mb-1">Body *</span>
            <textarea className="input" rows={4} value={form.body_text} onChange={e => set('body_text', e.target.value)}
              placeholder={'Hi {{1}}, your order {{2}} is on its way!'} /></label>
          <label className="block"><span className="block text-xs font-medium text-gray-500 mb-1">Footer (optional)</span>
            <input className="input" value={form.footer_text} onChange={e => set('footer_text', e.target.value)} placeholder="Reply STOP to opt out" /></label>
          <label className="block"><span className="block text-xs font-medium text-gray-500 mb-1">Buttons (one per line — "Label" or "Label | https://url")</span>
            <textarea className="input font-mono text-xs" rows={2} value={form.buttons} onChange={e => set('buttons', e.target.value)}
              placeholder={'Track order | https://example.com/track\nStop promotions'} /></label>
          <label className="block"><span className="block text-xs font-medium text-gray-500 mb-1">Example values for {'{{1}}, {{2}}'} (comma-separated)</span>
            <input className="input" value={form.example_params} onChange={e => set('example_params', e.target.value)} placeholder="Jane, #1234" /></label>
        </div>
        <div className="flex justify-end gap-2 mt-5">
          <button onClick={onClose} className="btn-secondary text-sm">Cancel</button>
          <button onClick={submit} disabled={busy} className="btn-primary text-sm disabled:opacity-60">{busy ? 'Saving…' : 'Save template'}</button>
        </div>
      </div>
    </div>
  )
}
